import * as React from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { manifest } from '@/lib/content';
import { personas } from '@/lib/personas';
import { cn } from '@/lib/utils';

export interface MegaMenuItem {
  label: string;
  href: string;
  description?: string;
  external?: boolean;
}

export interface MegaMenuColumn {
  heading: string;
  items: MegaMenuItem[];
}

export interface MegaMenuProps {
  label: string;
  /** Section index the menu's "Browse all" link points to (e.g. /learn). */
  href: string;
  columns: MegaMenuColumn[];
}

const HEADING_CLS =
  'px-2 pb-1.5 text-[10px] font-medium uppercase tracking-wider text-ink-soft';

/**
 * Accessible mega menu. The trigger is a single disclosure button (Radix
 * gives it aria-haspopup, aria-expanded, focus return and Escape); the panel
 * opens with a "Browse all" overview link first, then columns of real
 * DropdownMenuItem links so keyboard traversal, typeahead and screen-reader
 * menu semantics actually apply. Columns whose heading is empty render an
 * aria-hidden spacer so continuation columns line up with their siblings.
 */
export function MegaMenu({ label, href, columns }: MegaMenuProps) {
  const colCount = columns.length;
  const widthClass = colCount >= 3 ? 'w-[760px]' : 'w-[540px]';
  const gridClass =
    colCount >= 3 ? 'grid-cols-3' : colCount === 2 ? 'grid-cols-2' : 'grid-cols-1';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group inline-flex h-9 items-center gap-1 rounded-md px-2.5 text-sm font-medium text-ink-soft transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent data-[state=open]:bg-bg-mute data-[state=open]:text-ink"
        >
          {label}
          <ChevronDown
            className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180"
            strokeWidth={1.75}
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className={cn('p-4', widthClass)}>
        <DropdownMenuItem asChild className="mb-1 h-auto px-2 py-2">
          <Link to={href} className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-ink">Browse all {label}</span>
            <ArrowRight className="h-3.5 w-3.5 text-accent" strokeWidth={1.75} aria-hidden="true" />
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className={cn('grid gap-5 pt-2', gridClass)}>
          {columns.map((column, i) => {
            const items = column.items.map((item) => (
              <DropdownMenuItem
                key={item.href}
                asChild
                className="h-auto items-start px-2 py-1.5"
              >
                <MegaMenuLink item={item} />
              </DropdownMenuItem>
            ));
            // A heading-less column is the visual continuation of its
            // labelled sibling (the second half of a long list). Render it
            // outside a DropdownMenuGroup so a screen reader never announces
            // an unnamed group; the aria-hidden spacer keeps it aligned.
            if (!column.heading) {
              return (
                <div key={`col-${i}`}>
                  <div className={HEADING_CLS} aria-hidden="true" />
                  {items}
                </div>
              );
            }
            return (
              <DropdownMenuGroup key={column.heading}>
                <DropdownMenuLabel className={HEADING_CLS}>
                  {column.heading}
                </DropdownMenuLabel>
                {items}
              </DropdownMenuGroup>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * forwardRef + prop spread so Radix's DropdownMenuItem asChild can hand this
 * link the real menuitem semantics (role, roving tabindex, arrow traversal,
 * onSelect-to-close) via Slot. Without forwarding, those props would be
 * dropped and the item would behave like a plain link inside a menu shell.
 */
const MegaMenuLink = React.forwardRef<
  HTMLAnchorElement,
  { item: MegaMenuItem } & React.HTMLAttributes<HTMLAnchorElement>
>(function MegaMenuLink({ item, ...props }, ref) {
  const content = (
    <span className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-ink">{item.label}</span>
      {item.description ? (
        <span className="line-clamp-1 text-xs text-ink-mute">{item.description}</span>
      ) : null}
    </span>
  );

  if (item.external) {
    return (
      <a ref={ref} href={item.href} target="_blank" rel="noreferrer noopener" {...props}>
        {content}
      </a>
    );
  }
  return (
    <Link ref={ref} to={item.href} {...props}>
      {content}
    </Link>
  );
});

/* ------------------------------------------------------------------ *
 * Column data. The volatile lists (learn curriculum, persona tracks)
 * are derived from the content manifest and personas at module load, so
 * adding a section or a role can never orphan it in the nav again, and a
 * hand-typed stale slug (the old /start/cto-director 404) is impossible.
 * ------------------------------------------------------------------ */

function learnColumns(): MegaMenuColumn[] {
  const learn = manifest.sections
    .filter((s) => s.kind === 'learn')
    .slice()
    .sort((a, b) => a.order - b.order);
  const mid = Math.ceil(learn.length / 2);
  const toItems = (sections: typeof learn): MegaMenuItem[] =>
    sections.map((s) => ({
      label: s.title,
      href: `/learn/${s.slug}`,
      description: s.tagline,
    }));
  return [
    { heading: 'Curriculum', items: toItems(learn.slice(0, mid)) },
    { heading: '', items: toItems(learn.slice(mid)) },
    {
      heading: 'Quick links',
      items: [
        { label: 'Glossary', href: '/glossary', description: 'Every eval term, defined.' },
        { label: 'Reading list', href: '/papers', description: 'Curated papers by theme.' },
        { label: 'Downloads', href: '/downloads', description: 'Checklists and templates.' },
      ],
    },
  ];
}

const START_DESCRIPTIONS: Record<string, string> = {
  'principal-ai-engineer': 'Bound failure modes and sub-10-minute regressions.',
  'eng-manager': 'Stand up an eval program and staff it.',
  cto: 'Quality strategy, risk posture, and buy-vs-build.',
  'product-manager': 'Define quality bars and read eval results.',
  'sdet-qa': 'Regression suites and CI gates for LLM features.',
  'research-engineer': 'Rigorous benchmarks and statistical claims.',
  'data-engineer': 'Dataset lineage, versioning, and pipelines.',
  'backend-engineer': 'Instrument traces and wire the eval infra.',
  'frontend-engineer': 'Evaluate UI-facing AI features and feedback.',
};

function startColumns(): MegaMenuColumn[] {
  const items: MegaMenuItem[] = personas.map((p) => ({
    label: p.shortName,
    href: `/start/${p.slug}`,
    description: START_DESCRIPTIONS[p.slug],
  }));
  const mid = Math.ceil(items.length / 2);
  return [
    { heading: 'Pick your role', items: items.slice(0, mid) },
    { heading: '', items: items.slice(mid) },
  ];
}

export const LEARN_COLUMNS: MegaMenuColumn[] = learnColumns();

export const START_COLUMNS: MegaMenuColumn[] = startColumns();

export const TECHNIQUES_COLUMNS: MegaMenuColumn[] = [
  {
    heading: 'Judging methods',
    items: [
      { label: 'LLM-as-Judge', href: '/techniques/llm-as-judge', description: 'When to use, key biases, mitigations.' },
      { label: 'Pairwise comparison & Elo', href: '/techniques/pairwise-and-elo', description: 'Relative ranking when scores are noisy.' },
      { label: 'Rubric design', href: '/techniques/rubric-design', description: 'Criteria a judge can apply consistently.' },
      { label: 'Reference-based metrics', href: '/techniques/reference-based-metrics', description: 'BLEU, ROUGE, exact match and their limits.' },
      { label: 'Embedding similarity', href: '/techniques/embedding-similarity', description: 'Semantic scoring and its failure modes.' },
    ],
  },
  {
    heading: 'Data & rigor',
    items: [
      { label: 'Golden datasets', href: '/techniques/golden-datasets', description: 'Build and version a trustworthy set.' },
      { label: 'Error analysis', href: '/techniques/error-analysis', description: 'Turn failures into a measurable taxonomy.' },
      { label: 'Statistical methods', href: '/techniques/statistical-methods', description: 'Confidence intervals, bootstrap, power.' },
      { label: 'Robustness testing', href: '/techniques/robustness-testing', description: 'Format and order sensitivity checks.' },
      { label: 'Contamination detection', href: '/techniques/contamination-detection', description: 'Catch benchmark leakage before you trust it.' },
    ],
  },
  {
    heading: 'Agents & production',
    items: [
      { label: 'Agent trajectory eval', href: '/techniques/agent-trajectory-eval', description: 'Score tool calls and multi-step paths.' },
      { label: 'Online evaluation', href: '/techniques/online-evaluation', description: 'Judge production traffic safely.' },
      { label: 'Guardrails vs evals', href: '/techniques/guardrails-vs-evals', description: 'Where each belongs in the architecture.' },
    ],
  },
];

export const TOOLS_COLUMNS: MegaMenuColumn[] = [
  {
    heading: 'Platforms',
    items: [
      { label: 'LangSmith', href: '/tools/langsmith' },
      { label: 'Langfuse', href: '/tools/langfuse' },
      { label: 'Braintrust', href: '/tools/braintrust' },
      { label: 'Arize Phoenix', href: '/tools/arize-phoenix' },
      { label: 'Comet Opik', href: '/tools/comet-opik' },
      { label: 'W&B Weave', href: '/tools/w-and-b-weave' },
      { label: 'HoneyHive', href: '/tools/honeyhive' },
      { label: 'Galileo', href: '/tools/galileo' },
    ],
  },
  {
    heading: 'Libraries & harnesses',
    items: [
      { label: 'Inspect AI', href: '/tools/inspect-ai' },
      { label: 'lm-evaluation-harness', href: '/tools/lm-evaluation-harness' },
      { label: 'DeepEval (Confident AI)', href: '/tools/confident-ai-deepeval' },
      { label: 'Ragas', href: '/tools/ragas' },
      { label: 'Promptfoo', href: '/tools/promptfoo' },
      { label: 'TruLens', href: '/tools/trulens' },
      { label: 'Giskard', href: '/tools/giskard' },
      { label: 'OpenAI Evals', href: '/tools/openai-evals' },
      { label: 'LlamaIndex evals', href: '/tools/llamaindex-evals' },
    ],
  },
];

export default MegaMenu;
